/**
 * Atlas PM · 项目星图 · 数据 hooks
 *
 * 数据流：
 *   /projects/:id                → 项目核心名字
 *   /tasks/tree/:projectId       → 全部任务（树形，需拍平）
 *   /tasks/:id/graph             → 真实依赖边 + depth（接口返回的是**整个项目**的图，
 *                                  故取任一任务 id 即可拿到全项目 edges）
 *
 * ⚠️ `form` 字段当前接口未提供，聚类回退到 category；见 layout.ts 说明。
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { flattenTaskTree, layoutStar } from './layout';
import type { StarLayout, StarTaskInput } from './layout';
import type { StarEdgeSpec } from './edges';
import type {
  ProjectCompletenessInfo,
  ProjectDetail,
  ProjectListItem,
  TaskGraph,
  TaskTreeNode,
} from '@atlaspm/shared';

interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface StarData {
  project: ProjectDetail | undefined;
  tasks: StarTaskInput[];
  edges: StarEdgeSpec[];
  layout: StarLayout;
  /** ⭐ P6.54 项目级齐套率（%）；无「需附件要素」时为 null（前端显示「—」） */
  deliverableReadyPct: number | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/** 从依赖图 / 任务树组装边（去重，真实依赖优先） */
export function buildEdgeSpecs(
  tasks: readonly StarTaskInput[],
  graph: TaskGraph | undefined,
): StarEdgeSpec[] {
  const ids = new Set(tasks.map((t) => t.id));
  const seen = new Set<string>();
  const edges: StarEdgeSpec[] = [];

  const push = (from: string, to: string, kind: StarEdgeSpec['kind']): void => {
    if (from === to || !ids.has(from) || !ids.has(to)) return;
    const key = `${kind}:${from}->${to}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ from, to, kind });
  };

  // 真实依赖（graph 返回整个项目的边）
  for (const e of graph?.edges ?? []) {
    push(String(e.from), String(e.to), 'dep');
  }

  // 父子关系
  for (const t of tasks) {
    if (t.parentId) push(t.parentId, t.id, 'parent');
  }

  return edges;
}

export function useStarData(projectId: string | null): StarData {
  const projectQuery = useQuery({
    queryKey: qk.project(projectId ?? ''),
    queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`),
    enabled: Boolean(projectId),
  });

  const treeQuery = useQuery({
    queryKey: qk.taskTree(projectId ?? ''),
    queryFn: () => api.get<TaskTreeNode[]>(`/tasks/tree/${projectId}`),
    enabled: Boolean(projectId),
  });

  const tasks = useMemo<StarTaskInput[]>(
    () => (treeQuery.data ? flattenTaskTree(treeQuery.data) : []),
    [treeQuery.data],
  );

  const firstTaskId = tasks[0]?.id;

  // graph 接口返回的是任务所在**整个项目**的依赖图，取任一任务 id 即可
  const graphQuery = useQuery({
    queryKey: qk.taskGraph(firstTaskId ?? ''),
    queryFn: () => api.get<TaskGraph>(`/tasks/${firstTaskId}/graph`),
    enabled: Boolean(firstTaskId),
  });

  const edges = useMemo(
    () => buildEdgeSpecs(tasks, graphQuery.data),
    [tasks, graphQuery.data],
  );

  const layout = useMemo(
    () => layoutStar(projectQuery.data?.name ?? '', tasks),
    [projectQuery.data?.name, tasks],
  );

  /* ⭐ P6.54：项目级齐套率（实时口径，与任务级一致）。附件增删后 ProjectAggregateService
     会重算落库，但实时接口保证详情页永远拿到最新值。 */
  const completenessQuery = useQuery({
    queryKey: qk.projectCompleteness(projectId ?? ''),
    queryFn: () => api.get<ProjectCompletenessInfo>(`/projects/${projectId}/completeness`),
    enabled: Boolean(projectId),
  });

  return {
    project: projectQuery.data,
    tasks,
    edges,
    layout,
    deliverableReadyPct: completenessQuery.data?.completenessPct ?? null,
    isLoading: projectQuery.isLoading || treeQuery.isLoading,
    isError: projectQuery.isError || treeQuery.isError,
    error: (projectQuery.error as Error | null) ?? (treeQuery.error as Error | null),
  };
}

/** 用户直接访问 /star 且未选项目时，取第一个可见项目用于自动选中 */
export function useFirstProject() {
  return useQuery({
    queryKey: qk.projects({ purpose: 'star-first' }),
    queryFn: () => api.get<Paginated<ProjectListItem>>(`/projects${qs({ page: 1, pageSize: 1 })}`),
    staleTime: 60_000,
  });
}
